import React, {ReactElement} from "react";
import {
    MANAGE_CONTRACTORS_ROLES,
    MANAGE_INVENTORY_ROLES,
    MANAGE_OPERATIONS_ROLES,
    MANAGE_PRODUCTS_ROLES,
    MANAGE_PRODUCTION_ROLES,
    MANAGE_SETTINGS_ROLES,
    MANAGE_USERS_ROLES,
    MANAGE_WAREHOUSES_ROLES,
    READ_CONTRACTORS_ROLES,
    READ_INVENTORY_ROLES,
    READ_OPERATIONS_ROLES,
    READ_PRODUCTS_ROLES,
    READ_PRODUCTION_ROLES,
    READ_STOCK_ROLES,
    READ_WAREHOUSES_ROLES
} from "../helpers/applicationUserHelper";
import {
    AuditOutlined,
    BarcodeOutlined,
    CalculatorOutlined,
    CalendarOutlined,
    ClusterOutlined,
    ContactsOutlined,
    DashboardOutlined,
    DatabaseOutlined,
    DeploymentUnitOutlined,
    ExperimentOutlined,
    InboxOutlined,
    SettingOutlined,
    SwapOutlined,
    TeamOutlined
} from "@ant-design/icons";
import {ApplicationUserRole} from "../api";
import {RoutingPaths} from "../pages/routing/routingContainer";

export type NavigationSectionKey =
    | "start"
    | "warehouse"
    | "production"
    | "relationships"
    | "admin";

export interface NavigationSectionDefinition {
    key: NavigationSectionKey;
    label: string;
    color: string;
}

export interface NavigationItemDefinition {
    key: string;
    displayName: string;
    description: string;
    route: string;
    icon: ReactElement;
    section: NavigationSectionKey;
    allowedForRoles?: ApplicationUserRole[];
    mobilePlacement?: "upper" | "main" | "lower";
}

interface RouteActionDefinition {
    label: string;
    route: string;
    allowedForRoles?: ApplicationUserRole[];
}

export interface RouteContextDefinition {
    routePattern: string;
    title: string;
    description: string;
    section: NavigationSectionKey;
    navRoute?: string;
    actions?: RouteActionDefinition[];
}

export const navigationSections: NavigationSectionDefinition[] = [
    {
        key: "start",
        label: "Start i przegląd",
        color: "#7b5b30"
    },
    {
        key: "warehouse",
        label: "Magazyn",
        color: "#385c6b"
    },
    {
        key: "production",
        label: "Produkcja",
        color: "#556b2f"
    },
    {
        key: "relationships",
        label: "Relacje",
        color: "#824d36"
    },
    {
        key: "admin",
        label: "Administracja",
        color: "#5b5f77"
    }
];

export const navigationItems: NavigationItemDefinition[] = [
    {
        key: "home",
        displayName: "Strona główna",
        description: "Panel startowy z najważniejszymi informacjami i skrótami.",
        route: RoutingPaths.mainPage.route,
        icon: <DashboardOutlined/>,
        section: "start",
        mobilePlacement: "upper"
    },
    {
        key: "calendar",
        displayName: "Kalendarz",
        description: "Widok terminów i zaplanowanych działań.",
        route: RoutingPaths.warehouseOperationsCalendar.route,
        icon: <CalendarOutlined/>,
        section: "start",
        allowedForRoles: READ_OPERATIONS_ROLES,
        mobilePlacement: "upper"
    },
    {
        key: "general-stock",
        displayName: "Stany magazynowe",
        description: "Szybki podgląd stanów produktów i partii.",
        route: RoutingPaths.stockGeneralTable.route,
        icon: <InboxOutlined/>,
        section: "warehouse",
        allowedForRoles: READ_STOCK_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "warehouses",
        displayName: "Magazyny",
        description: "Struktura magazynów, podrzędne lokalizacje i etykiety.",
        route: RoutingPaths.warehouseTable.route,
        icon: <DatabaseOutlined/>,
        section: "warehouse",
        allowedForRoles: READ_WAREHOUSES_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "products",
        displayName: "Produkty",
        description: "Katalog produktów, ich parametry i stany.",
        route: RoutingPaths.productTable.route,
        icon: <BarcodeOutlined/>,
        section: "warehouse",
        allowedForRoles: READ_PRODUCTS_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "stock-operations",
        displayName: "Operacje magazynowe",
        description: "PZ, WZ, MM, PW, RW oraz publikacja i historia zmian.",
        route: RoutingPaths.stockOperationTable.route,
        icon: <SwapOutlined/>,
        section: "warehouse",
        allowedForRoles: READ_OPERATIONS_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "inventory-operations",
        displayName: "Inwentaryzacje",
        description: "Spisy, publikacja wyników i przejście do szczegółów.",
        route: RoutingPaths.inventoryOperationTable.route,
        icon: <AuditOutlined/>,
        section: "warehouse",
        allowedForRoles: READ_INVENTORY_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "production-processes",
        displayName: "Procesy produkcyjne",
        description: "Nadzór nad etapami produkcji i badaniami procesu.",
        route: RoutingPaths.productionProcessTable.route,
        icon: <ExperimentOutlined/>,
        section: "production",
        allowedForRoles: READ_PRODUCTION_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "crops",
        displayName: "Uprawy",
        description: "Lista upraw i przejście do szczegółów zbiorów.",
        route: RoutingPaths.cropTable.route,
        icon: <ClusterOutlined/>,
        section: "production",
        allowedForRoles: READ_PRODUCTION_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "strains",
        displayName: "Szczepy",
        description: "Szczepy używane w produktach i procesach.",
        route: RoutingPaths.strainTable.route,
        icon: <DeploymentUnitOutlined/>,
        section: "production",
        allowedForRoles: MANAGE_PRODUCTS_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "contractors",
        displayName: "Kontrahenci",
        description: "Relacje handlowe, spotkania i historia operacji.",
        route: RoutingPaths.contractorTable.route,
        icon: <ContactsOutlined/>,
        section: "relationships",
        allowedForRoles: READ_CONTRACTORS_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "users",
        displayName: "Użytkownicy",
        description: "Zarządzanie użytkownikami i ich historią zmian.",
        route: RoutingPaths.userTable.route,
        icon: <TeamOutlined/>,
        section: "admin",
        allowedForRoles: MANAGE_USERS_ROLES,
        mobilePlacement: "lower"
    },
    {
        key: "units",
        displayName: "Jednostki miary",
        description: "Jednostki bazowe i pomocnicze używane w systemie.",
        route: RoutingPaths.unitOfMeasureTable.route,
        icon: <CalculatorOutlined/>,
        section: "admin",
        allowedForRoles: MANAGE_PRODUCTS_ROLES,
        mobilePlacement: "lower"
    },
    {
        key: "company-data",
        displayName: "Dane firmy",
        description: "Dane organizacji, ustawienia i historia zmian.",
        route: RoutingPaths.companyData.route,
        icon: <SettingOutlined/>,
        section: "admin",
        allowedForRoles: MANAGE_SETTINGS_ROLES,
        mobilePlacement: "lower"
    }
];

export const routeContexts: RouteContextDefinition[] = [
    {
        routePattern: RoutingPaths.mainPage.route,
        title: "Strona główna",
        description: "Tutaj najłatwiej zacząć pracę: masz szybkie wejścia do modułów i najważniejsze widgety operacyjne.",
        section: "start",
        navRoute: RoutingPaths.mainPage.route,
        actions: [
            {label: "Stany magazynowe", route: RoutingPaths.stockGeneralTable.route, allowedForRoles: READ_STOCK_ROLES},
            {label: "Procesy produkcyjne", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES},
            {label: "Kontrahenci", route: RoutingPaths.contractorTable.route, allowedForRoles: READ_CONTRACTORS_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.warehouseOperationsCalendar.route,
        title: "Kalendarz",
        description: "Widok terminów i spotkań w jednym miejscu, pomocny przy planowaniu pracy zespołu.",
        section: "start",
        navRoute: RoutingPaths.warehouseOperationsCalendar.route
    },
    {
        routePattern: RoutingPaths.stockGeneralTable.route,
        title: "Stany magazynowe",
        description: "Przegląd stanów produktów i partii. Dobry punkt wejścia, gdy użytkownik nie wie jeszcze, który magazyn lub produkt wybrać.",
        section: "warehouse",
        navRoute: RoutingPaths.stockGeneralTable.route,
        actions: [
            {label: "Magazyny", route: RoutingPaths.warehouseTable.route, allowedForRoles: READ_WAREHOUSES_ROLES},
            {label: "Produkty", route: RoutingPaths.productTable.route, allowedForRoles: READ_PRODUCTS_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.warehouseTable.route,
        title: "Magazyny",
        description: "Zarządzanie strukturą magazynów i przejście do stanów lub nowych operacji z poziomu konkretnego miejsca.",
        section: "warehouse",
        navRoute: RoutingPaths.warehouseTable.route,
        actions: [
            {label: "Stany magazynowe", route: RoutingPaths.stockGeneralTable.route, allowedForRoles: READ_STOCK_ROLES},
            {label: "Dodaj magazyn", route: RoutingPaths.warehouseAdd.route, allowedForRoles: MANAGE_WAREHOUSES_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.subWarehouseTable.route,
        title: "Magazyny podrzędne",
        description: "Widok zawężony do konkretnej gałęzi magazynu, żeby łatwiej utrzymać porządek w strukturze.",
        section: "warehouse",
        navRoute: RoutingPaths.warehouseTable.route,
        actions: [
            {label: "Wróć do magazynów", route: RoutingPaths.warehouseTable.route, allowedForRoles: READ_WAREHOUSES_ROLES},
            {label: "Dodaj magazyn", route: RoutingPaths.warehouseAdd.route, allowedForRoles: MANAGE_WAREHOUSES_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.warehouseAdd.route,
        title: "Nowy magazyn",
        description: "Formularz tworzenia magazynu. Po zapisaniu wrócisz do listy i łatwo przejdziesz do stanów lub operacji.",
        section: "warehouse",
        navRoute: RoutingPaths.warehouseTable.route,
        actions: [{label: "Lista magazynów", route: RoutingPaths.warehouseTable.route, allowedForRoles: READ_WAREHOUSES_ROLES}]
    },
    {
        routePattern: RoutingPaths.subWarehouseAdd.route,
        title: "Nowy magazyn podrzędny",
        description: "Dodanie podrzędnej lokalizacji bez gubienia kontekstu całej struktury magazynowej.",
        section: "warehouse",
        navRoute: RoutingPaths.warehouseTable.route,
        actions: [{label: "Lista magazynów", route: RoutingPaths.warehouseTable.route, allowedForRoles: READ_WAREHOUSES_ROLES}]
    },
    {
        routePattern: RoutingPaths.warehouseEdit.route,
        title: "Edycja magazynu",
        description: "Zmiana danych magazynu przy zachowaniu szybkiego powrotu do listy i stanów.",
        section: "warehouse",
        navRoute: RoutingPaths.warehouseTable.route,
        actions: [{label: "Lista magazynów", route: RoutingPaths.warehouseTable.route, allowedForRoles: READ_WAREHOUSES_ROLES}]
    },
    {
        routePattern: RoutingPaths.warehouseDetails.route,
        title: "Historia magazynu",
        description: "Szczegóły i historia zmian wybranego magazynu, żeby łatwiej zrozumieć co się zmieniło.",
        section: "warehouse",
        navRoute: RoutingPaths.warehouseTable.route,
        actions: [{label: "Lista magazynów", route: RoutingPaths.warehouseTable.route, allowedForRoles: READ_WAREHOUSES_ROLES}]
    },
    {
        routePattern: RoutingPaths.productTable.route,
        title: "Produkty",
        description: "Katalog produktów z szybkim przejściem do stanów, historii i etykiet.",
        section: "warehouse",
        navRoute: RoutingPaths.productTable.route,
        actions: [
            {label: "Stany magazynowe", route: RoutingPaths.stockGeneralTable.route, allowedForRoles: READ_STOCK_ROLES},
            {label: "Dodaj produkt", route: RoutingPaths.productAdd.route, allowedForRoles: MANAGE_PRODUCTS_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.productAdd.route,
        title: "Nowy produkt",
        description: "Tworzenie produktu z myślą o dalszej pracy na stanach, partiach i etykietach.",
        section: "warehouse",
        navRoute: RoutingPaths.productTable.route,
        actions: [{label: "Lista produktów", route: RoutingPaths.productTable.route, allowedForRoles: READ_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.productEdit.route,
        title: "Edycja produktu",
        description: "Zmiana danych produktu bez konieczności wracania do kilku osobnych ekranów.",
        section: "warehouse",
        navRoute: RoutingPaths.productTable.route,
        actions: [{label: "Lista produktów", route: RoutingPaths.productTable.route, allowedForRoles: READ_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.productUpdateHistory.route,
        title: "Historia produktu",
        description: "Podgląd zmian produktu i szybki powrót do katalogu produktów.",
        section: "warehouse",
        navRoute: RoutingPaths.productTable.route,
        actions: [{label: "Lista produktów", route: RoutingPaths.productTable.route, allowedForRoles: READ_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.productStockTable.route,
        title: "Stany produktu",
        description: "Widok stanów dla jednego produktu, przydatny gdy użytkownik zaczyna od produktu zamiast magazynu.",
        section: "warehouse",
        navRoute: RoutingPaths.productTable.route,
        actions: [
            {label: "Produkty", route: RoutingPaths.productTable.route, allowedForRoles: READ_PRODUCTS_ROLES},
            {label: "Stany magazynowe", route: RoutingPaths.stockGeneralTable.route, allowedForRoles: READ_STOCK_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.productBatchStockTable.route,
        title: "Stan partii",
        description: "Szczegół partii produktu. To widok diagnostyczny, gdy trzeba szybko zejść na poziom partii.",
        section: "warehouse",
        navRoute: RoutingPaths.productTable.route,
        actions: [{label: "Produkty", route: RoutingPaths.productTable.route, allowedForRoles: READ_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.productBatchUnitStockScan.route,
        title: "Stan partii i jednostki",
        description: "Szczegół partii w konkretnej jednostce z rozbiciem na magazyny i historię ruchów.",
        section: "warehouse",
        navRoute: RoutingPaths.productTable.route,
        actions: [{label: "Produkty", route: RoutingPaths.productTable.route, allowedForRoles: READ_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.productStockHistory.route,
        title: "Historia stanu",
        description: "Historia zmian stanu produktu w magazynie, pomocna przy weryfikacji ruchów.",
        section: "warehouse",
        navRoute: RoutingPaths.stockGeneralTable.route
    },
    {
        routePattern: RoutingPaths.warehouseStockTable.route,
        title: "Stan magazynu",
        description: "Widok stanów konkretnego magazynu z możliwością zejścia do szczegółów produktów i partii.",
        section: "warehouse",
        navRoute: RoutingPaths.warehouseTable.route,
        actions: [
            {label: "Magazyny", route: RoutingPaths.warehouseTable.route, allowedForRoles: READ_WAREHOUSES_ROLES},
            {label: "Stany magazynowe", route: RoutingPaths.stockGeneralTable.route, allowedForRoles: READ_STOCK_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.stockReservationHistory.route,
        title: "Historia rezerwacji",
        description: "Widok pomocniczy do wyjaśniania zmian rezerwacji dla produktu i magazynu.",
        section: "warehouse",
        navRoute: RoutingPaths.stockGeneralTable.route
    },
    {
        routePattern: RoutingPaths.stockOperationTable.route,
        title: "Operacje magazynowe",
        description: "Lista wszystkich ruchów magazynowych z publikacją, historią i szybkim filtrowaniem.",
        section: "warehouse",
        navRoute: RoutingPaths.stockOperationTable.route,
        actions: [
            {label: "Dodaj operację", route: RoutingPaths.stockOperationAddNoParams.route, allowedForRoles: MANAGE_OPERATIONS_ROLES},
            {label: "Dodaj operację produkcyjną", route: RoutingPaths.productionStockOperationAddNoParams.route, allowedForRoles: MANAGE_PRODUCTION_ROLES},
            {label: "Kalendarz", route: RoutingPaths.warehouseOperationsCalendar.route, allowedForRoles: READ_OPERATIONS_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.stockOperationAdd.route,
        title: "Nowa operacja magazynowa",
        description: "Formularz ruchu magazynowego. Użytkownik powinien od razu widzieć, że jest to osobny krok od przeglądania listy.",
        section: "warehouse",
        navRoute: RoutingPaths.stockOperationTable.route,
        actions: [{label: "Lista operacji", route: RoutingPaths.stockOperationTable.route, allowedForRoles: READ_OPERATIONS_ROLES}]
    },
    {
        routePattern: RoutingPaths.stockOperationAddNoParams.route,
        title: "Nowa operacja magazynowa",
        description: "Formularz ruchu magazynowego bez wstępnie wybranego magazynu lub procesu.",
        section: "warehouse",
        navRoute: RoutingPaths.stockOperationTable.route,
        actions: [{label: "Lista operacji", route: RoutingPaths.stockOperationTable.route, allowedForRoles: READ_OPERATIONS_ROLES}]
    },
    {
        routePattern: RoutingPaths.stockOperationProcessAdd.route,
        title: "Operacja dla procesu",
        description: "Dodawanie operacji magazynowej w kontekście konkretnego procesu produkcyjnego.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Procesy produkcyjne", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.stockOperationEdit.route,
        title: "Edycja operacji magazynowej",
        description: "Poprawa operacji bez gubienia dostępu do listy i historii zmian.",
        section: "warehouse",
        navRoute: RoutingPaths.stockOperationTable.route,
        actions: [{label: "Lista operacji", route: RoutingPaths.stockOperationTable.route, allowedForRoles: READ_OPERATIONS_ROLES}]
    },
    {
        routePattern: RoutingPaths.stockOperationDetailsTable.route,
        title: "Szczegóły operacji magazynowej",
        description: "Pełny widok jednej operacji: dokument, status i pozycje towarowe w jednym miejscu.",
        section: "warehouse",
        navRoute: RoutingPaths.stockOperationTable.route,
        actions: [{label: "Lista operacji", route: RoutingPaths.stockOperationTable.route, allowedForRoles: READ_OPERATIONS_ROLES}]
    },
    {
        routePattern: RoutingPaths.stockOperationHistory.route,
        title: "Historia operacji magazynowej",
        description: "Podgląd kolejnych zmian operacji dla szybkiej diagnozy kto i co zmieniał.",
        section: "warehouse",
        navRoute: RoutingPaths.stockOperationTable.route,
        actions: [{label: "Lista operacji", route: RoutingPaths.stockOperationTable.route, allowedForRoles: READ_OPERATIONS_ROLES}]
    },
    {
        routePattern: RoutingPaths.inventoryOperationTable.route,
        title: "Inwentaryzacje",
        description: "Lista spisów i publikacji. Wyraźnie oddzielony obszar od zwykłych operacji magazynowych.",
        section: "warehouse",
        navRoute: RoutingPaths.inventoryOperationTable.route,
        actions: [
            {label: "Dodaj inwentaryzację", route: RoutingPaths.inventoryOperationAdd.route, allowedForRoles: MANAGE_INVENTORY_ROLES},
            {label: "Magazyny", route: RoutingPaths.warehouseTable.route, allowedForRoles: READ_WAREHOUSES_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.inventoryOperationAdd.route,
        title: "Nowa inwentaryzacja",
        description: "Formularz spisu z jasnym powrotem do listy inwentaryzacji.",
        section: "warehouse",
        navRoute: RoutingPaths.inventoryOperationTable.route,
        actions: [{label: "Lista inwentaryzacji", route: RoutingPaths.inventoryOperationTable.route, allowedForRoles: READ_INVENTORY_ROLES}]
    },
    {
        routePattern: RoutingPaths.inventoryOperationEdit.route,
        title: "Edycja inwentaryzacji",
        description: "Zmiana nieopublikowanej inwentaryzacji bez mieszania tego z innymi operacjami.",
        section: "warehouse",
        navRoute: RoutingPaths.inventoryOperationTable.route,
        actions: [{label: "Lista inwentaryzacji", route: RoutingPaths.inventoryOperationTable.route, allowedForRoles: READ_INVENTORY_ROLES}]
    },
    {
        routePattern: RoutingPaths.productionProcessTable.route,
        title: "Procesy produkcyjne",
        description: "Główne miejsce do śledzenia etapów produkcji, badań i przejść do kolejnych operacji.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [
            {label: "Dodaj proces", route: RoutingPaths.productionProcessAdd.route, allowedForRoles: MANAGE_PRODUCTION_ROLES},
            {label: "Dodaj operację produkcyjną", route: RoutingPaths.productionStockOperationAddNoParams.route, allowedForRoles: MANAGE_PRODUCTION_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.productionProcessAdd.route,
        title: "Nowy proces produkcyjny",
        description: "Formularz procesu z szybkim powrotem do centralnej listy procesów.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Lista procesów", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.productionProcessEdit.route,
        title: "Edycja procesu",
        description: "Zmiana danych procesu bez tracenia orientacji w jego miejscu w całym obszarze produkcji.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Lista procesów", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.processStockOperationDetails.route,
        title: "Szczegóły procesu",
        description: "Centrum szczegółów procesu: etapy, operacje i badania w jednym widoku.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [
            {label: "Lista procesów", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.productionStockOperationAddNoParams.route,
        title: "Nowa operacja produkcyjna",
        description: "Formularz operacji produkcyjnej dla użytkowników, którzy chcą zacząć od działania, a nie od listy procesów.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Procesy produkcyjne", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.productionStockOperationEdit.route,
        title: "Edycja operacji produkcyjnej",
        description: "Korekta operacji produkcyjnej z jasnym powiązaniem do całego obszaru produkcji.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Procesy produkcyjne", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.productionStockOperationDetailsTable.route,
        title: "Szczegóły operacji produkcyjnej",
        description: "Widok szczegółów dla operacji produkcyjnej lub złożonych operacji powiązanych z produkcją.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Procesy produkcyjne", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.internalStockOperationDetailsTable.route,
        title: "Szczegóły operacji wewnętrznej",
        description: "Widok szczegółów przesunięć i operacji wewnętrznych pomiędzy magazynami.",
        section: "warehouse",
        navRoute: RoutingPaths.stockOperationTable.route,
        actions: [{label: "Lista operacji", route: RoutingPaths.stockOperationTable.route, allowedForRoles: READ_OPERATIONS_ROLES}]
    },
    {
        routePattern: RoutingPaths.vinificationStockOperationForm.route,
        title: "Operacja etapu produkcji",
        description: "Formularz etapu winifikacji prowadzony w kontekście procesu produkcyjnego.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Procesy produkcyjne", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.physicochemicalTestTable.route,
        title: "Badania fizykochemiczne",
        description: "Wyniki badań przypisanych do procesu produkcyjnego. To pomocniczy, ale ważny ekran produkcyjny.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Procesy produkcyjne", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.physicochemicalTestAdd.route,
        title: "Nowe badanie fizykochemiczne",
        description: "Dodanie wyniku badania wprost do procesu produkcyjnego.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Procesy produkcyjne", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.physicochemicalTestEdit.route,
        title: "Edycja badania fizykochemicznego",
        description: "Aktualizacja wyniku badania bez opuszczania kontekstu procesu.",
        section: "production",
        navRoute: RoutingPaths.productionProcessTable.route,
        actions: [{label: "Procesy produkcyjne", route: RoutingPaths.productionProcessTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.cropTable.route,
        title: "Uprawy",
        description: "Lista upraw z szybkim wejściem do szczegółów i powiązanych szczepów.",
        section: "production",
        navRoute: RoutingPaths.cropTable.route,
        actions: [
            {label: "Dodaj uprawę", route: RoutingPaths.cropAdd.route, allowedForRoles: MANAGE_PRODUCTION_ROLES},
            {label: "Szczepy", route: RoutingPaths.strainTable.route, allowedForRoles: MANAGE_PRODUCTION_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.cropAdd.route,
        title: "Nowa uprawa",
        description: "Formularz uprawy z czytelnym powrotem do listy oraz szczepów.",
        section: "production",
        navRoute: RoutingPaths.cropTable.route,
        actions: [{label: "Lista upraw", route: RoutingPaths.cropTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.cropEdit.route,
        title: "Edycja uprawy",
        description: "Zmiana danych uprawy bez rozpraszania użytkownika dodatkowymi ekranami.",
        section: "production",
        navRoute: RoutingPaths.cropTable.route,
        actions: [{label: "Lista upraw", route: RoutingPaths.cropTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.cropDetails.route,
        title: "Szczegóły uprawy",
        description: "Widok szczegółowy uprawy z zachowaniem kontekstu całego modułu produkcji.",
        section: "production",
        navRoute: RoutingPaths.cropTable.route,
        actions: [{label: "Lista upraw", route: RoutingPaths.cropTable.route, allowedForRoles: READ_PRODUCTION_ROLES}]
    },
    {
        routePattern: RoutingPaths.strainTable.route,
        title: "Szczepy",
        description: "Lista szczepów używanych w produktach i procesach produkcyjnych.",
        section: "production",
        navRoute: RoutingPaths.strainTable.route,
        actions: [
            {label: "Dodaj szczep", route: RoutingPaths.strainAdd.route, allowedForRoles: MANAGE_PRODUCTS_ROLES},
            {label: "Uprawy", route: RoutingPaths.cropTable.route, allowedForRoles: READ_PRODUCTION_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.strainAdd.route,
        title: "Nowy szczep",
        description: "Formularz szczepu jako osobny etap przygotowania danych do produktów i produkcji.",
        section: "production",
        navRoute: RoutingPaths.strainTable.route,
        actions: [{label: "Lista szczepów", route: RoutingPaths.strainTable.route, allowedForRoles: READ_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.strainEdit.route,
        title: "Edycja szczepu",
        description: "Zmiana danych szczepu z szybkim powrotem do listy.",
        section: "production",
        navRoute: RoutingPaths.strainTable.route,
        actions: [{label: "Lista szczepów", route: RoutingPaths.strainTable.route, allowedForRoles: READ_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.strainDetails.route,
        title: "Szczegóły szczepu",
        description: "Widok informacyjny dla szczepu używanego dalej w produktach i procesach.",
        section: "production",
        navRoute: RoutingPaths.strainTable.route,
        actions: [{label: "Lista szczepów", route: RoutingPaths.strainTable.route, allowedForRoles: READ_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.contractorTable.route,
        title: "Kontrahenci",
        description: "Moduł relacji z kontrahentami, spotkaniami i historią współpracy.",
        section: "relationships",
        navRoute: RoutingPaths.contractorTable.route,
        actions: [{label: "Dodaj kontrahenta", route: RoutingPaths.contractorAdd.route, allowedForRoles: MANAGE_CONTRACTORS_ROLES}]
    },
    {
        routePattern: RoutingPaths.contractorAdd.route,
        title: "Nowy kontrahent",
        description: "Formularz nowego kontrahenta z szybkim powrotem do listy relacji.",
        section: "relationships",
        navRoute: RoutingPaths.contractorTable.route,
        actions: [{label: "Lista kontrahentów", route: RoutingPaths.contractorTable.route, allowedForRoles: READ_CONTRACTORS_ROLES}]
    },
    {
        routePattern: RoutingPaths.contractorEdit.route,
        title: "Edycja kontrahenta",
        description: "Zmiana danych kontrahenta bez gubienia historii spotkań i operacji.",
        section: "relationships",
        navRoute: RoutingPaths.contractorTable.route,
        actions: [{label: "Lista kontrahentów", route: RoutingPaths.contractorTable.route, allowedForRoles: READ_CONTRACTORS_ROLES}]
    },
    {
        routePattern: RoutingPaths.contractorDetails.route,
        title: "Szczegóły kontrahenta",
        description: "Jedno miejsce dla danych, spotkań i powiązanych działań z kontrahentem.",
        section: "relationships",
        navRoute: RoutingPaths.contractorTable.route,
        actions: [{label: "Lista kontrahentów", route: RoutingPaths.contractorTable.route, allowedForRoles: READ_CONTRACTORS_ROLES}]
    },
    {
        routePattern: RoutingPaths.contractorHistory.route,
        title: "Historia kontrahenta",
        description: "Historia zmian danych kontrahenta dla łatwiejszego audytu.",
        section: "relationships",
        navRoute: RoutingPaths.contractorTable.route,
        actions: [{label: "Lista kontrahentów", route: RoutingPaths.contractorTable.route, allowedForRoles: READ_CONTRACTORS_ROLES}]
    },
    {
        routePattern: RoutingPaths.contractorOperationHistory.route,
        title: "Historia operacji kontrahenta",
        description: "Widok operacji powiązanych z kontrahentem jako osobny kontekst roboczy.",
        section: "relationships",
        navRoute: RoutingPaths.contractorTable.route,
        actions: [{label: "Lista kontrahentów", route: RoutingPaths.contractorTable.route, allowedForRoles: READ_CONTRACTORS_ROLES}]
    },
    {
        routePattern: RoutingPaths.userTable.route,
        title: "Użytkownicy",
        description: "Administracja kontami użytkowników i ich historią zmian.",
        section: "admin",
        navRoute: RoutingPaths.userTable.route,
        actions: [{label: "Dodaj użytkownika", route: RoutingPaths.userAdd.route, allowedForRoles: MANAGE_USERS_ROLES}]
    },
    {
        routePattern: RoutingPaths.userAdd.route,
        title: "Nowy użytkownik",
        description: "Formularz użytkownika z czytelnym powrotem do listy administracyjnej.",
        section: "admin",
        navRoute: RoutingPaths.userTable.route,
        actions: [{label: "Lista użytkowników", route: RoutingPaths.userTable.route, allowedForRoles: MANAGE_USERS_ROLES}]
    },
    {
        routePattern: RoutingPaths.userEdit.route,
        title: "Edycja użytkownika",
        description: "Zmiana danych użytkownika bez zlewania tego z innymi ustawieniami systemu.",
        section: "admin",
        navRoute: RoutingPaths.userTable.route,
        actions: [{label: "Lista użytkowników", route: RoutingPaths.userTable.route, allowedForRoles: MANAGE_USERS_ROLES}]
    },
    {
        routePattern: RoutingPaths.userDetails.route,
        title: "Historia użytkownika",
        description: "Szczegóły i historia zmian konkretnego użytkownika.",
        section: "admin",
        navRoute: RoutingPaths.userTable.route,
        actions: [{label: "Lista użytkowników", route: RoutingPaths.userTable.route, allowedForRoles: MANAGE_USERS_ROLES}]
    },
    {
        routePattern: RoutingPaths.unitOfMeasureTable.route,
        title: "Jednostki miary",
        description: "Zarządzanie jednostkami bazowymi i pomocniczymi używanymi w całym systemie.",
        section: "admin",
        navRoute: RoutingPaths.unitOfMeasureTable.route,
        actions: [{label: "Dodaj jednostkę", route: RoutingPaths.unitOfMeasureAdd.route, allowedForRoles: MANAGE_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.unitOfMeasureAdd.route,
        title: "Nowa jednostka miary",
        description: "Formularz jednostki z szybkim powrotem do listy administracyjnej.",
        section: "admin",
        navRoute: RoutingPaths.unitOfMeasureTable.route,
        actions: [{label: "Lista jednostek", route: RoutingPaths.unitOfMeasureTable.route, allowedForRoles: MANAGE_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.unitOfMeasureEdit.route,
        title: "Edycja jednostki miary",
        description: "Aktualizacja jednostki używanej potem w wielu modułach systemu.",
        section: "admin",
        navRoute: RoutingPaths.unitOfMeasureTable.route,
        actions: [{label: "Lista jednostek", route: RoutingPaths.unitOfMeasureTable.route, allowedForRoles: MANAGE_PRODUCTS_ROLES}]
    },
    {
        routePattern: RoutingPaths.companyData.route,
        title: "Dane firmy",
        description: "Dane organizacji i ustawienia wspólne dla systemu, oddzielone od zwykłej pracy operacyjnej.",
        section: "admin",
        navRoute: RoutingPaths.companyData.route,
        actions: [
            {label: "Edytuj dane firmy", route: RoutingPaths.companyDataEdit.route, allowedForRoles: MANAGE_SETTINGS_ROLES},
            {label: "Historia zmian", route: RoutingPaths.companyDataHistory.route, allowedForRoles: MANAGE_SETTINGS_ROLES}
        ]
    },
    {
        routePattern: RoutingPaths.companyDataEdit.route,
        title: "Edycja danych firmy",
        description: "Zmiana ustawień i danych organizacji bez mieszania ich z modułami operacyjnymi.",
        section: "admin",
        navRoute: RoutingPaths.companyData.route,
        actions: [{label: "Dane firmy", route: RoutingPaths.companyData.route, allowedForRoles: MANAGE_SETTINGS_ROLES}]
    },
    {
        routePattern: RoutingPaths.companyDataHistory.route,
        title: "Historia danych firmy",
        description: "Widok historii zmian dla danych firmy i ustawień globalnych.",
        section: "admin",
        navRoute: RoutingPaths.companyData.route,
        actions: [{label: "Dane firmy", route: RoutingPaths.companyData.route, allowedForRoles: MANAGE_SETTINGS_ROLES}]
    }
];

export function isAllowedForRoles(
    item: Pick<NavigationItemDefinition, "allowedForRoles"> | Pick<RouteActionDefinition, "allowedForRoles">,
    roles?: ApplicationUserRole[]
) {
    if (!item.allowedForRoles || item.allowedForRoles.length === 0) {
        return true;
    }

    return item.allowedForRoles.some(role => roles?.includes(role));
}

export function getAvailableNavigationItems(roles?: ApplicationUserRole[]) {
    return navigationItems.filter(item => isAllowedForRoles(item, roles));
}

export function getNavigationItemsBySection(roles?: ApplicationUserRole[]) {
    const availableItems = getAvailableNavigationItems(roles);

    return navigationSections.map(section => ({
        ...section,
        items: availableItems.filter(item => item.section === section.key)
    })).filter(section => section.items.length > 0);
}

export function getMobileNavigationItems(roles?: ApplicationUserRole[]) {
    const availableItems = getAvailableNavigationItems(roles);

    return {
        upper: availableItems.filter(item => item.mobilePlacement === "upper"),
        main: availableItems.filter(item => item.mobilePlacement === "main"),
        lower: availableItems.filter(item => item.mobilePlacement === "lower")
    };
}

export function getRouteContextByPattern(routePattern?: string) {
    if (!routePattern) {
        return undefined;
    }

    return routeContexts.find(context => context.routePattern === routePattern);
}

export function getSectionDefinition(sectionKey: NavigationSectionKey) {
    return navigationSections.find(section => section.key === sectionKey)!;
}

export function getNavigationItemByRoute(route?: string, roles?: ApplicationUserRole[]) {
    if (!route) {
        return undefined;
    }

    return getAvailableNavigationItems(roles).find(item => item.route === route);
}

export function getRelatedNavigationItems(
    sectionKey: NavigationSectionKey,
    roles?: ApplicationUserRole[],
    excludedRoute?: string
) {
    return getAvailableNavigationItems(roles)
        .filter(item => item.section === sectionKey && item.route !== excludedRoute)
        .slice(0, 4);
}

export function getVisibleActions(
    context: RouteContextDefinition,
    roles?: ApplicationUserRole[]
) {
    return (context.actions ?? []).filter(action => isAllowedForRoles(action, roles));
}
